<script lang="ts">
	import BuyCampfireLink from "@components/BuyCampfireLink.svelte";
	import Button from "@components/common/Button.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import basket from "@core/assets/icons/basket.svg?raw";
	import flame from "@core/assets/icons/flame.svg";
	import menuIcon from "@core/assets/icons/menu.svg?raw";
	import type { MenuItem } from "@core/schema/MenuItem.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getPriceOfOrder } from "@core/schema/utils/getPriceOfOrder.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { getCartStore } from "../../contexts/cartContextKey.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { headbarHeight } from "../../state/headbarHeight.svelte.js";
	import Menu from "./Menu.svelte";

	const {
		menu,
		useTransparentMenu,
	}: {
		menu: MenuItem[];
		useTransparentMenu: boolean;
	} = $props();

	let mobileMenuOpen = $state(false);
	let scrollY = $state(0);

	const transparentMenu = $derived(useTransparentMenu && scrollY <= 50);
	const { tenant } = getGlobalTemplateProps();
	const cartStore = getCartStore();
</script>

<svelte:window bind:scrollY />
<div
	class={[
		"fixed inset-x-0 transition-all duration-300 top-0 h-26 from-black/35 pointer-events-none z-30 bg-gradient-to-b",
		transparentMenu ? "opacity-100" : "opacity-0",
	]}
></div>
<header
	bind:clientHeight={headbarHeight.value}
	class={["fixed bg-transparent inset-x-0 top-0 z-40 flex flex-col transition-all duration-300"]}
>
	<div
		class={[
			"grid-container transition-all duration-300 border-b",
			{
				"border-transparent": transparentMenu,
				"border-nov-light-200 pb-px bg-white": !transparentMenu,
			},
		]}
	>
		<div
			class="flex px-0 xl:!px-4 xl:full-width 2xl:container-12 justify-between gap-4 items-center transition-all duration-300 py-2 md:py-0 xl:mt-1"
		>
			<a href={getHomepagePath(tenant)} class="flex items-center gap-1 min-w-39">
				<span class="font-semibold -tracking-[0.005em] text-[1.25rem]/6">
					<Media
						alt="🔥"
						width={32}
						height={32}
						source={{ src: flame, type: "image/svg+xml" }}
						class="size-[1em] -translate-y-[0.025em]"
					/>
				</span>
				<span
					class={[
						transparentMenu && "text-white",
						"-tracking-[0.005em] text-2md/6 font-bold transition-all duration-300",
					]}
				>
					Zažehnuto.cz
				</span>
			</a>
			<div
				class={[
					"flex items-center gap-2 lg:gap-8 rounded-full md:p-3 md:pl-5 lg:pl-8 transition-all duration-300 lg:bg-white",
				]}
			>
				<Menu bind:mobileOpen={mobileMenuOpen} items={menu} />
				<BuyCampfireLink baseWidth class="hidden xl:block" variant={ButtonVariant.Secondary} noIcon />
				<Button
					variant={ButtonVariant.Secondary}
					class="!size-10 !p-1 sm:!p-2 rounded-full xl:hidden bg-white"
					onclick={(): void => {
						mobileMenuOpen = !mobileMenuOpen;
					}}
				>
					<Icon icon={menuIcon} class="w-6 h-6 sm:w-6 sm:h-6 text-black" />
				</Button>
			</div>
			<Link
				class="hidden xl:flex items-center xl:justify-between xl:min-w-26 gap-1 !py-2.5 !px-4 !text-xs normal-case"
				variant={ButtonVariant.Filled}
				href={getCartPath(tenant.locale)}
			>
				<Icon icon={basket} class="size-5 text-white" />
				{formatPrice(getPriceOfOrder($cartStore.value.order).totalPrice.withTax, tenant.locale, false)}
			</Link>
		</div>
	</div>
</header>
